import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import { APIProvider } from '@vis.gl/react-google-maps';
import DropdownCustom from '../controls/DropdownCustom';
import { useData } from '../context/data.context';
import {
  ActionData,
  ActionsTypes,
  DeviceAttributes,
  DeviceAttributesV1,
  DeviceType,
  Group,
  Option
} from '../constants/telosair-interfaces';
import DetailedFloor from './DetailedFloor';
import GroupMap from './MapGroup';
import { TOAST_MESSAGES } from '../constants/global-constants';
import { deleteDevice } from '../services/dashboard-service';

import AddDeviceModal from '../modals/AddDevice';

const options: Option<ActionsTypes>[] = [
  { name: 'Edit device', id: 'edit' },
  { name: 'Delete device', id: 'delete' }
];

type ExpandedGroupProps = {
  devices: DeviceAttributesV1[]; // TODO: Change any to DeviceAttributes.
  group: Group;
  setIsGroupModalOpen: (isOpen: boolean) => void;
};

/**
 * Shows an expanded group with sensors details.
 * @param props - ExpandedGroupProps.
 * @returns Expanded Group react component.
 */
export default function ExpandedGroup(props: ExpandedGroupProps): ReactElement {
  const { devices: mainDeviceList, fetchAllDevices, groupedData: groups } = useData();
  const { devices, group, setIsGroupModalOpen } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<DeviceAttributes>>(
    {} as ActionData<DeviceAttributes>
  );
  const mappedDevicesList = devices.map((item) => ({
    ...item,
    data: mainDeviceList.filter((device) => device.serialNumber === item.serial_number)[0]
  }));

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteDevice = async (id: number): Promise<void> => {
    try {
      await deleteDevice(id);
      await fetchAllDevices();
      toast.success(TOAST_MESSAGES.deleted);
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleDeviceAction = ({ type, data }: ActionData<DeviceAttributesV1>): void => {
    if (type === 'delete') {
      handleDeleteDevice(data.id);
    } else if (type === 'edit') {
      const device = mainDeviceList.find(
        (deviceData) => deviceData.serialNumber === data.serial_number
      );
      setActionData({ type: 'edit', data: device as DeviceAttributes });
      setIsModalOpen(true);
    }
  };

  /**
   * Function to handle Device Modal.
   * @param newDeviceAdded - To check weather a New device is added or not.
   */
  const handleDeviceModalClose = (newDeviceAdded: boolean): void => {
    if (newDeviceAdded) {
      fetchAllDevices();
    }
    setIsModalOpen(false);
    setActionData({} as ActionData<DeviceAttributes>);
  };

  return (
    <>
      <tr className="w-full table-row lg:hidden">
        <td colSpan={6} className="col-span-6">
          <div className="flex justify-center">
            <div className="h-[200px] w-full p-4">
              {group.floorPlan === DeviceType.OUTDOOR ? (
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                  <GroupMap
                    selectedParameters={[]}
                    onDashboard
                    showExpandedView={false}
                    selectedGroup={group}
                  />
                </APIProvider>
              ) : (
                <DetailedFloor
                  selectedParameters={[]}
                  onDashboard
                  showExpandedView={false}
                  selectedGroup={group}
                />
              )}
            </div>
          </div>
        </td>
      </tr>
      <tr className="w-full bg-white dark:bg-container-700 text-gray-700 dark:text-gray-300 border-b border-b-gray-300 dark:border-b-gray-900">
        <td className="hidden lg:table-cell">
          <div className="flex justify-center">
            <div className="h-[200px] w-full p-4">
              {group.floorPlan === DeviceType.OUTDOOR ? (
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
                  <GroupMap
                    selectedParameters={[]}
                    onDashboard
                    showExpandedView={false}
                    selectedGroup={group}
                  />
                </APIProvider>
              ) : (
                <DetailedFloor
                  selectedParameters={[]}
                  onDashboard
                  showExpandedView={false}
                  selectedGroup={group}
                />
              )}
            </div>
          </div>
        </td>
        <td
          colSpan={6}
          className="lg:col-span-5 align-top border-l border-gray-300 dark:border-gray-900"
        >
          <table className="w-full divide-y divide-gray-300 dark:divide-gray-900">
            <tr className="text-gray-400 text-xs font-medium  border-gray-300 dark:border-gray-900">
              <th className="h-10 py-3 text-center">Device Name</th>
              <th className="py-3 text-center">Type</th>
              <th className="py-3 text-center">Position</th>
              <th className="py-3 text-center">Connection</th>
              <th className="py-3 text-center">Action</th>
            </tr>
            {mappedDevicesList.map((device) => (
              <tr
                key={device.id}
                className=" text-gray-500 dark:text-gray-300 border-gray-300 dark:border-gray-900"
              >
                <td className="w-[12%] py-3 text-center">
                  {device.data?.deviceName} (#{device.data?.serialNumber})
                </td>
                <td className="w-[12%] py-3 text-center">{device.type}</td>
                <td className="w-[12%] py-3 text-center">-</td>
                <td className="w-[12%] py-3 text-center">
                  -
                  <div className="text-gray-500 text-xs">
                    -{/* ({moment(device.updatedDate).fromNow()}) */}
                  </div>
                </td>
                <td className="w-[12%] py-3 text-center">
                  <DropdownCustom<ActionsTypes>
                    onClick={(buttonId) => handleDeviceAction({ type: buttonId, data: device })}
                    controlPlaceholder="Action"
                    buttons={options}
                  />
                </td>
              </tr>
            ))}
          </table>
        </td>
      </tr>
      {isModalOpen && (
        <AddDeviceModal
          actionData={actionData}
          groups={groups}
          setIsGroupModalOpen={setIsGroupModalOpen}
          handleClose={handleDeviceModalClose}
        />
      )}
    </>
  );
}
